import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { FiSearch } from 'react-icons/fi';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import './ChatInbox.css';

const headers = () => ({ Authorization: `Bearer ${getAuthToken()}` });

const SendIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
    <path fill="currentColor" d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
  </svg>
);

const formatTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
};

const PartnerUserChat = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = Number(searchParams.get('user') || 0);
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [denied, setDenied] = useState(false);
  const logRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      axios.get(`${API_URL}/admin/chat/partner-users`, {
        headers: headers(),
        params: query.trim() ? { q: query.trim() } : {},
      }).then((res) => {
        if (cancelled) return;
        setDenied(false);
        setUsers(Array.isArray(res.data?.users) ? res.data.users : []);
      }).catch((error) => {
        if (!cancelled && error.response?.status === 403) setDenied(true);
      });
    };
    const timer = setTimeout(load, 200);
    const poll = setInterval(load, 4000);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      clearInterval(poll);
    };
  }, [query]);

  useEffect(() => {
    if (!selectedId) {
      setMessages([]);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/chat/partner-users/${selectedId}/messages`, { headers: headers() });
        if (cancelled) return;
        setDenied(false);
        setMessages(Array.isArray(res.data?.messages) ? res.data.messages : []);
        axios.post(`${API_URL}/admin/chat/partner-users/${selectedId}/read`, {}, { headers: headers() }).catch(() => {});
      } catch (error) {
        if (!cancelled && error.response?.status === 403) setDenied(true);
      }
    };
    load();
    const timer = setInterval(load, 2000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [selectedId]);

  useEffect(() => {
    const node = logRef.current;
    if (node) node.scrollTop = node.scrollHeight;
  }, [messages]);

  const selected = useMemo(
    () => users.find((user) => user.id === selectedId) || null,
    [users, selectedId],
  );

  const send = async (event) => {
    event.preventDefault();
    const body = text.trim();
    if (!body || sending || !selectedId) return;
    setSending(true);
    try {
      const res = await axios.post(
        `${API_URL}/admin/chat/partner-users/${selectedId}/messages`,
        { body },
        { headers: headers() },
      );
      setMessages(Array.isArray(res.data?.messages) ? res.data.messages : []);
      setText('');
      const staff = res.data?.user || {};
      setUsers((prev) => prev.map((user) => (
        user.id === selectedId
          ? {
            ...user,
            last_message: body,
            last_message_at: new Date().toISOString(),
            last_staff_name: staff.last_staff_name || user.last_staff_name,
            last_staff_role: staff.last_staff_role || user.last_staff_role,
          }
          : user
      )));
    } catch (error) {
      if (error.response?.status === 403) setDenied(true);
    } finally {
      setSending(false);
    }
  };

  if (denied) {
    return (
      <div className="admin-page">
        <div className="page-header">
          <h1>User Chat</h1>
          <p>User Chat Access is off, or Chat Access is off, so this partner cannot message users.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page chat-inbox-page">
      <div className="page-header">
        <h1>User Chat</h1>
        <p>Every user is listed here. Messages go between this partner and the user.</p>
      </div>
      <div className={`chat-inbox${selectedId ? ' thread-open' : ''}`}>
        <aside className="chat-thread-list">
          <div className="chat-list-search">
            <FiSearch aria-hidden="true" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search users"
              aria-label="Search users"
            />
          </div>
          {users.length === 0 && <p className="chat-empty">No users found.</p>}
          {[...users].sort((a, b) => {
            const aAt = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
            const bAt = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
            if (bAt !== aAt) return bAt - aAt;
            return String(a.name || '').localeCompare(String(b.name || ''));
          }).map((user) => (
            <button
              type="button"
              key={user.id}
              className={`chat-thread${user.id === selectedId ? ' is-active' : ''}`}
              onClick={() => setSearchParams({ user: String(user.id) })}
            >
              <span className="chat-thread__top">
                <strong>{user.name || 'User'}</strong>
                {user.admin_unread > 0 && <em>{user.admin_unread}</em>}
              </span>
              <span className="chat-thread__preview">
                {user.last_message || (user.chat_access ? 'No message yet' : 'Chat access off')}
              </span>
              {user.last_staff_name ? <span className="chat-thread__staff">Last: {user.last_staff_name}</span> : null}
              <span className="chat-thread__time">{formatTime(user.last_message_at)}</span>
            </button>
          ))}
        </aside>
        <section className="chat-pane">
          {!selectedId && <p className="chat-empty">Select a user to start chatting.</p>}
          {selectedId > 0 && (
            <>
              <header className="chat-pane__head">
                <strong>{selected?.name || 'User'}</strong>
                {selected && (
                  <p>
                    {selected.email}
                    {selected.last_staff_name ? ` · Last: ${selected.last_staff_name}` : ''}
                    {selected.chat_access ? '' : ' · Chat access is off, so this user cannot reply yet.'}
                  </p>
                )}
              </header>
              <div className="chat-pane__log" ref={logRef}>
                {messages.filter((message) => message.sender !== 'system' && !message.deleted).map((message) => {
                  const side = message.sender === 'admin' ? 'admin' : 'user';
                  return (
                    <div key={message.id} className={`chat-msg-row chat-msg-row--${side}`}>
                      <div className={`chat-msg chat-msg--${side}`}>
                        {message.sender === 'admin' && message.sender_name ? <span className="chat-msg__who">{message.sender_name}</span> : null}
                        <span>{message.body}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <form className="chat-pane__compose" onSubmit={send}>
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Write a message"
                  aria-label="Message"
                />
                <button type="submit" disabled={sending || !text.trim()} aria-label="Send">
                  <SendIcon />
                </button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default PartnerUserChat;
